


import { Component, Vue, Watch } from "vue-property-decorator";

import { Filter } from "@Internal/Contracts";
import { AdsPublications } from "App";
import { EventBus, $t } from "App/IOC";
import { Column, PagerSorter, SelectionFilter, TextFilter } from "App/Models";

import RecordsComponent from "Web/Components/Records.vue";
import Record from "Web/Components/Record.vue";

/* Full-page attribution list for a portal, reached from the list's Actions > Groupes. One row per
   group with Statut, Nom du client (société), Nom et prénom du owner, and an Attribuer checkbox.
   Subscribe/unsubscribe is written immediately on toggle, same API as the form's Groupes field.
   Semantics: no box checked -> every group sees the portal; at least one checked -> only the
   checked ones see it.
   Data source is /gateway/customer-list (id, company, name, group, status). The list is small (a
   few dozen rows), so filtering, status default ("actif") and pagination run client-side. */
@Component({
    components: {
        Records: RecordsComponent,
        Record
    }
})
export default class AdsPublicationGroupsView extends Vue {
    $refs!: Vue["$refs"] & {
        Records: RecordsComponent,
    };

    private portal: any = null;

    private allRows: any[] = [];
    private records: any[] = [];

    /* Subscribed customer ids (as strings). Matched the same way the form does (by customer Id),
       since portal.Customers reliably carries Id; the Group field is used only to write. */
    private assignedIds: string[] = [];

    private columns: Column[] = [];
    private pagerSorter: PagerSorter = new PagerSorter( 15 );

    private get portalId(): number {
        return Number( this.$route.params.id );
    }

    private async created(): Promise<void> {
        this.columns = [
            Column.Static( "Assign", $t( "ADS_PUBLICATION_columnAssign" ) ),
            Column.Static( "Owner", $t( "ADS_PUBLICATION_columnOwner" ) ),
            Column.Static( "Company", $t( "ADS_PUBLICATION_columnCompany" ) ),
            Column.Static( "Status", $t( "ADS_PUBLICATION_columnStatus" ) )
        ];

        await this.setup();
    }

    /* Reload when stepping to a sibling portal through the header prev/next arrows. */
    @Watch( "$route" )
    private onRouteChange( to: any, from: any ): void {
        if( to.name !== "ads-publication-groups" || from.params.id === to.params.id )
            return;

        this.portal = null;
        this.setup();
    }

    /* Status options + a name search, with "actif" applied by default (see applyFilters). */
    private getFilters(): Filter[] {
        return [
            new SelectionFilter(
                "Status",
                $t( "ADS_PUBLICATION_filterStatus" ),
                [
                    { Id: "active", Name: $t( "ADS_PUBLICATION_statusActive" ) },
                    { Id: "inactive", Name: $t( "ADS_PUBLICATION_statusInactive" ) }
                ],
                $t( "ADS_PUBLICATION_filterStatus" )
            ),
            new SelectionFilter(
                "Assigned",
                $t( "ADS_PUBLICATION_filterAssigned" ),
                [
                    { Id: "yes", Name: $t( "ADS_PUBLICATION_columnAssigned" ) },
                    { Id: "no", Name: $t( "ADS_PUBLICATION_assignedNo" ) }
                ],
                $t( "ADS_PUBLICATION_filterAssigned" )
            ),
            new TextFilter(
                "Name",
                $t( "ADS_PUBLICATION_columnName" ),
                $t( "ADS_PUBLICATION_columnName" )
            )
        ];
    }

    private async setup(): Promise<void> {
        const [ portal, error ] = await AdsPublications.Get( this.portalId );

        if( error !== undefined || portal === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.portal = portal;

        /* Get() returns the subscribed customers under the raw lowercase "customers" key (items
           shaped { id, group, name }); the form capitalises them itself. Read both shapes so the
           pre-check works whichever the deserializer hands back. */
        const subscribed = ( portal as any ).Customers || ( portal as any ).customers || [];
        this.assignedIds = subscribed.map( ( item: any ) => String( item.Id || item.id ) );

        const name = this.headline;
        if( name !== "" )
            document.title = name;

        await this.loadRows();
    }

    private get headline(): string {
        if( this.portal === null )
            return "";

        return this.portal.name || this.portal.Name || "";
    }

    private get image(): string {
        if( this.portal === null )
            return "";

        return this.portal.image || this.portal.Image || "";
    }

    private async loadRows(): Promise<void> {
        if( this.$refs.Records !== undefined )
            this.$refs.Records.Loading();

        const [ result, , error ] =
            await AdsPublications.GetAllCustomers( [], new PagerSorter( 999 ) );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.allRows = result;
        this.update( this.getFilters(), this.pagerSorter, true );
    }

    /* Records emits this on filter/sort/page change. We filter, default status to "actif", then
       paginate client-side and feed the requested page back to Records. */
    private update(
        filters: Filter[],
        pagerSorter: PagerSorter,
        resetPagination?: boolean
    ): void {
        if( resetPagination )
            pagerSorter.Page = 1;

        const filtered = this.applyFilters( filters );

        pagerSorter.Total = filtered.length;
        this.pagerSorter = pagerSorter;

        const start = ( pagerSorter.Page - 1 ) * pagerSorter.Limit;
        this.records = filtered.slice( start, start + pagerSorter.Limit );
    }

    private firstFilterId( filter: Filter | undefined ): string {
        if( filter === undefined )
            return "";

        const value = filter.FormatValue();
        const ids = Array.isArray( value ) ? value : [ value ];
        const cleaned = ids
            .map( id => String( id ) )
            .filter( id => id !== "" && id !== "undefined" );

        return cleaned.length > 0 ? cleaned[ 0 ] : "";
    }

    private applyFilters( filters: Filter[] ): any[] {
        const statusFilter = filters.find( filter => filter.Id === "Status" );
        const assignedFilter = filters.find( filter => filter.Id === "Assigned" );
        const nameFilter = filters.find( filter => filter.Id === "Name" );

        /* Assignment chip drives the filter; no chip -> show everything (so removing it lists all
           groups, not just the assigned ones). */
        const assignedMode = this.firstFilterId( assignedFilter );

        /* No explicit status chip -> show active groups only (Fred's default). */
        let statuses: string[] = [ "active" ];

        if( statusFilter !== undefined ) {
            const value = statusFilter.FormatValue();
            const ids = Array.isArray( value ) ? value : [ value ];
            const cleaned = ids
                .map( id => String( id ) )
                .filter( id => id !== "" && id !== "undefined" );

            if( cleaned.length > 0 )
                statuses = cleaned;
        }

        let keyword = "";
        if( nameFilter !== undefined ) {
            const value = nameFilter.FormatValue();
            keyword = String( Array.isArray( value ) ? value[ 0 ] || "" : value ).toLowerCase();
        }

        return this.allRows.filter( row => {
            if( statuses.indexOf( String( row.Status ) ) === -1 )
                return false;

            if( assignedMode === "yes" && !this.isAssigned( row ) )
                return false;

            if( assignedMode === "no" && this.isAssigned( row ) )
                return false;

            if( keyword !== "" ) {
                const haystack = `${row.Company} ${row.Owner}`.toLowerCase();
                if( haystack.indexOf( keyword ) === -1 )
                    return false;
            }

            return true;
        } );
    }

    private isAssigned( row: any ): boolean {
        return this.assignedIds.indexOf( String( row.Id ) ) !== -1;
    }

    /* Write the assignment immediately. On error we leave local state untouched so the checkbox
       stays in sync with the server. */
    private async toggleAssign( row: any ): Promise<void> {
        const data: any = { Id: this.portalId, GroupId: row.Group };

        if( this.isAssigned( row ) ) {
            const [ , error ] = await AdsPublications.DeleteCustomer( this.portalId, data );

            if( error !== undefined ) {
                EventBus.Publish( "Error", error );
                return;
            }

            this.assignedIds = this.assignedIds.filter( id => id !== String( row.Id ) );
            return;
        }

        const [ , error ] = await AdsPublications.CustomerCreate( data );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.assignedIds = [ ...this.assignedIds, String( row.Id ) ];
    }

    private statusLabel( status: string ): string {
        return status === "active"
            ? $t( "ADS_PUBLICATION_statusActive" )
            : $t( "ADS_PUBLICATION_statusInactive" );
    }
}
